import { pipe } from 'fp-ts/function';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { array, nonEmptyArray, string } from 'fp-ts';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { JobExecutorData } from '@mews-commander/data-access';
import { mapOption } from '@mews-commander/legacy-data-structures';
import { useCommanderSelector } from '@mews-commander/utils';
import { commanderForm, CheckboxGroupField, presence, SelectField, validation } from '@mews-commander/legacy-form';
import { FieldsContainer } from '@mews-commander/ui';
import { submitJobExecutionForm } from '../actions';
import { JobFormValues } from '../types';
import { TEAMS_FIELD_NAME } from '../constants';

const constraints = {
    [TEAMS_FIELD_NAME]: { presence },
};

interface DispatchProps {
    onSubmit: (values: JobFormValues) => void;
}

interface OwnProps {
    jobExecutors: Array<JobExecutorData>;
    form: string;
}

const mapExecutorOptions = (executors: Array<JobExecutorData>) =>
    pipe(
        executors,
        array.map((executor) => ({
            value: executor.name,
            label: `${executor.name} (${executor.defaultParameters.length})`,
        })),
        mapOption
    );

const FormFields = ({ jobExecutors, form }: OwnProps) => {
    const selectedTeams = useCommanderSelector((state) => formValueSelector(form)(state, TEAMS_FIELD_NAME)) || [];

    const teams = pipe(
        jobExecutors,
        array.map((executor) => executor.team),
        array.uniq(string.Eq),
        array.sort(string.Ord)
    );
    const teamOptions = pipe(
        teams,
        array.map((team) => ({ value: team, label: team })),
        mapOption
    );

    const teamJobExecutors = nonEmptyArray.groupBy((executor: JobExecutorData) => executor.team)(jobExecutors);

    return (
        <FieldsContainer>
            <SelectField
                label="Team"
                showEmptyValue
                options={teamOptions}
                name={TEAMS_FIELD_NAME}
                required
                multiselect
            />
            {selectedTeams.map((team: string) => (
                <CheckboxGroupField
                    key={team}
                    name={team}
                    label={team}
                    options={mapExecutorOptions(teamJobExecutors[team])}
                />
            ))}
        </FieldsContainer>
    );
};

export const JobExecutorsForm = pipe(
    FormFields,
    commanderForm({
        submitLabel: <LocalizedText textKey={TextKey.Submit} />,
        submitButtonProps: { variant: 'primary' },
        validate: validation(constraints),
    }),
    connect<{}, DispatchProps, OwnProps, ApplicationState>(
        undefined,
        (dispatch: CommanderThunkDispatch, { jobExecutors }: OwnProps) => ({
            onSubmit: async (values: JobFormValues) => {
                await dispatch(submitJobExecutionForm(values, jobExecutors));
            },
        })
    )
);
